import React, { useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useSidePanel } from '@/context/SidePanelContext';
import { SheetContent, SheetFooter } from '@/components/common/Sheet';
import { Button } from '@/components/common/Button';
import FixedIncomeOverview from '@/components/features/securities/FixedIncome/FixedIncomeOverview';
import { useGetSecurityTradingDataQuery, AssetPriceType } from '@fintronners/react-api/src';
import useUserAccount from '@/hooks/api/useUserAccount';
import useCachedMarketData from '@fintronners/react-widgets/src/hooks/market/useCachedMarketData';
import useTradeApiService from '@/hooks/api/useTradeService';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  fixedIncomeTradeSchema,
  FixedIncomeTradeFormValues,
  FixedIncomeTradeFormKeys,
} from './schema';
import { NumberNoNaN } from '@fintronners/react-utils/src/numberUtilsTSX';
import { TradeSide } from '../schema';
import { FixedIncomeTradeForm } from './FixedIncomeTradeForm';
import { FixedIncomeTradeSuccess } from './FixedIncomeTradeSuccess';
import { FixedIncomeTradeError } from './FixedIncomeTradeError';
import { FIXED_INCOME_LIMIT_BUY_PRICE_MARGIN } from '@fintronners/internal-core/src/trade/constants';
import {
  V1CreateTradeRequest,
  V1TradeExpirationType,
  V1MarketSide,
} from '@fintronners/react-api/src';
import FixedIncomeTradeSummary from '@/components/common/FixedIncomeTradeSummary/FixedIncomeTradeSummary';
import useCurrentAccountType from '@/hooks/utils/useCurrentAccountType';
import { usePreviewOrderCostStore } from '@/stores/trades/preview-order-cost';
import { usePreviewTradeFeeStore } from '@/stores/trades/preview-trade-fee';
import { TradeFormSteps } from '../constants';
import usePreviewTradeFee from '@fintronners/react-widgets/src/hooks/trades/usePreviewTradeFee';
import usePreviewOrderCost from '@fintronners/react-widgets/src/hooks/trades/usePreviewOrderCost';
import useGrowthBookFeature from '@fintronners/react-config/src/hooks/useGrowthBookFeature';
import { FeatureFlags } from '@fintronners/react-config/src/Config/growthbook';
import { useFixedIncomeTradeBreakdown } from '@/hooks/trade/useFixedIncomeTradeBreakdown';
import { getFixedIncomeTradeAmountErrorMessage } from '@fintronners/internal-core/src/trade/utils';

const DEFAULT_VALUES: FixedIncomeTradeFormValues = {
  [FixedIncomeTradeFormKeys.SIDE]: TradeSide.BUY,
  [FixedIncomeTradeFormKeys.LIMIT_PRICE]: 0,
  [FixedIncomeTradeFormKeys.AMOUNT]: 0,
};

/**
 * Fixed Income Side Panel Component
 * This component handles the fixed income trading flow within a side panel.
 *
 * It includes the following steps:
 * 1. Overview of the fixed income security
 * 2. Trade form for entering order details
 * 3. Confirmation of the trade details
 * 4. Success message after trade execution
 * 5. Error handling if trade fails
 */
export const FixedIncomeSidePanel = () => {
  const {
    currentFlowStep,
    sidePanelProps,
    goTo,
    closeSidePanel,
    setShowCloseButton,
    setShowBackLink,
  } = useSidePanel();

  const goToSummaryPanel = () => goTo(TradeFormSteps.SUMMARY);
  const goToSuccessPanel = () => goTo(TradeFormSteps.SUCCESS);
  const goToErrorPanel = () => goTo(TradeFormSteps.ERROR);

  const params = useSearchParams();
  const securityId = params.get('security-id') ?? undefined;

  const accountType = useCurrentAccountType();
  const { userAccount } = useUserAccount(accountType);

  const { fiatAsset: fiat } = useCachedMarketData();
  const fiatId = fiat?.id ?? '';

  const { data } = useGetSecurityTradingDataQuery({
    skip: !securityId || !userAccount?.firstPortfolioId || !fiatId,
    fetchPolicy: 'no-cache',
    variables: {
      securityId: securityId ?? '',
      balanceIds: [fiatId, securityId ?? ''],
      portfolioId: userAccount?.firstPortfolioId ?? '',
    },
  });

  const {
    data: previewOrderCost,
    loading: isPreviewOrderCostLoading,
    reset: resetOrderCost,
    setData: setPreviewOrderCostData,
    setError: setPreviewOrderCostError,
    setLoading: setPreviewOrderCostLoading,
  } = usePreviewOrderCostStore();
  const {
    loading: isPreviewTradeFeeLoading,
    reset: resetTradeFee,
    setData: setPreviewTradeFeeData,
    setError: setPreviewTradeFeeError,
    setLoading: setPreviewTradeFeeLoading,
  } = usePreviewTradeFeeStore();

  const isFeesAndCommissionsEnabled = useGrowthBookFeature(
    FeatureFlags.isFeesAndCommissionsEnabled,
  );
  const isFixedIncomeEnabled = useGrowthBookFeature(FeatureFlags.isFixedIncomeEnabled);

  const isPreviewLoading = isPreviewOrderCostLoading || isPreviewTradeFeeLoading;

  const tradeService = useTradeApiService();

  const { t } = useTranslation();

  const assetData = data?.securityAssets?.edges?.[0]?.node ?? undefined;
  const assetDetails = assetData?.fixedIncomeDetails;
  const market = assetData?.fixedIncomeMarket;

  const askPercentageOfParPrice = useMemo(
    () =>
      market?.askPrices.find((price) => price.type === AssetPriceType.AssetPriceTypePercentageOfPar)
        ?.price,
    [market],
  );

  const bidPercentageOfParPrice = useMemo(
    () =>
      market?.bidPrices.find((price) => price.type === AssetPriceType.AssetPriceTypePercentageOfPar)
        ?.price,
    [market],
  );

  const askPriceWithMargin = askPercentageOfParPrice
    ? Number(askPercentageOfParPrice) + FIXED_INCOME_LIMIT_BUY_PRICE_MARGIN
    : 0;

  const bidPriceWithMargin = bidPercentageOfParPrice
    ? Number(bidPercentageOfParPrice) - FIXED_INCOME_LIMIT_BUY_PRICE_MARGIN
    : 0;

  const fiatBalances = data?.userAccountPortfolioAssets?.edges?.find(
    (edge) => edge?.node?.asset?.id === fiatId,
  )?.node?.balances;

  const securityBalances = data?.userAccountPortfolioAssets?.edges?.find(
    (edge) => edge?.node?.asset?.id === securityId,
  )?.node?.balances;

  const tradableUnits =
    sidePanelProps.side === TradeSide.BUY
      ? NumberNoNaN(fiatBalances?.tradeable)
      : NumberNoNaN(securityBalances?.tradeable);

  const form = useForm({
    mode: 'onChange',
    resolver: yupResolver(fixedIncomeTradeSchema),
    defaultValues: DEFAULT_VALUES,
    values: {
      ...DEFAULT_VALUES,
      [FixedIncomeTradeFormKeys.SIDE]: sidePanelProps.side,
      [FixedIncomeTradeFormKeys.LIMIT_PRICE]:
        sidePanelProps.side === TradeSide.BUY ? askPriceWithMargin : bidPriceWithMargin,
    },
  });
  const [rawAmount, rawLimitPrice] = useWatch({
    control: form.control,
    name: [FixedIncomeTradeFormKeys.AMOUNT, FixedIncomeTradeFormKeys.LIMIT_PRICE],
  });
  const amount = NumberNoNaN(rawAmount);
  const limitPrice = NumberNoNaN(rawLimitPrice);
  const assetPrice = (limitPrice / 100) * NumberNoNaN(assetDetails?.parValue);

  const onPreviewTradeFeeRequestInvoked = () => setPreviewTradeFeeLoading(true);
  const onPreviewTradeFeeRequestEnd = () => setPreviewTradeFeeLoading(false);
  const onPreviewTradeFeeRequestError = (error: Error) => {
    setPreviewTradeFeeError(error);
    goToErrorPanel();
  };

  const { getTradeFee, abortGetTradeFee } = usePreviewTradeFee({
    withDebounce: true,
    shouldSkip: !isFeesAndCommissionsEnabled,
    apiService: tradeService,
    onSuccess: setPreviewTradeFeeData,
    onError: onPreviewTradeFeeRequestError,
    onRequestInvoked: onPreviewTradeFeeRequestInvoked,
    onRequestEnd: onPreviewTradeFeeRequestEnd,
  });

  const getPreviewTradeFee = async (accruedInterest?: number) => {
    const tradeFeeAmount = amount * (assetPrice + NumberNoNaN(accruedInterest));

    await getTradeFee({
      amount: tradeFeeAmount.toString(),
      assetUid: securityId || '',
    });
  };

  const onPreviewOrderCostRequestInvoked = () => setPreviewOrderCostLoading(true);
  const onPreviewOrderCostRequestEnd = () => setPreviewOrderCostLoading(false);
  const onPreviewOrderCostRequestError = (error: Error) => {
    setPreviewOrderCostError(error);
    goToErrorPanel();
  };

  const { getOrderCost, abortGetOrderCost } = usePreviewOrderCost({
    withDebounce: true,
    shouldSkip: !isFixedIncomeEnabled,
    apiService: tradeService,
    onSuccess: async (orderCostResponse) => {
      setPreviewOrderCostData(orderCostResponse);

      const accruedInterest = NumberNoNaN(orderCostResponse.estimatedAccruedInterest);
      await getPreviewTradeFee(accruedInterest);
    },
    onError: onPreviewOrderCostRequestError,
    onRequestInvoked: onPreviewOrderCostRequestInvoked,
    onRequestEnd: onPreviewOrderCostRequestEnd,
  });

  useEffect(() => {
    if (!amount || !limitPrice) {
      abortGetTradeFee();
      abortGetOrderCost();
      setPreviewTradeFeeData(null);
      setPreviewOrderCostData(null);
      return;
    }

    getOrderCost({
      assetUid: securityId ?? '',
      portfolioUid: userAccount?.firstPortfolioId ?? '',
      parValue: assetDetails?.parValue,
      price: assetPrice.toFixed(4).toString(),
      quantity: amount.toString(),
    });
  }, [
    securityId,
    userAccount?.firstPortfolioId,
    assetDetails?.parValue,
    limitPrice,
    assetPrice,
    amount,
    getOrderCost,
    abortGetTradeFee,
    abortGetOrderCost,
    setPreviewTradeFeeData,
    setPreviewOrderCostData,
  ]);

  const onContinue = useCallback(() => {
    if (!previewOrderCost) return;
    goToSummaryPanel();
  }, [previewOrderCost, goTo]);

  const onSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const _limitPrice = form.getValues(FixedIncomeTradeFormKeys.LIMIT_PRICE);
    const _amount = form.getValues(FixedIncomeTradeFormKeys.AMOUNT);
    const limit = (_limitPrice / 100) * NumberNoNaN(assetDetails?.parValue);

    const tradeRequest: V1CreateTradeRequest = {
      portfolioUid: userAccount?.firstPortfolioId,
      tradeOrder: {
        side: sidePanelProps.side === TradeSide.BUY ? V1MarketSide.Buy : V1MarketSide.Sell,
        instrument: {
          base: securityId,
          quote: fiatId,
        },
        base: _amount.toString(),
        limit: {
          limit: limit.toFixed(4).toString(),
        },
        expirationType: V1TradeExpirationType.Day,
        fixedIncome: {
          parValue: assetDetails?.parValue,
        },
      },
    };

    try {
      const response = await tradeService.tradeServiceCreateTrade(tradeRequest);

      if (!response.data?.orderUid) {
        throw Error('Order creation failed: No order UID returned');
      }

      goToSuccessPanel();
      setShowBackLink(false);
    } catch (error) {
      const sideLabel = sidePanelProps.side === TradeSide.BUY ? 'buy' : 'sell';
      console.error(`Error creating fixed income limit ${sideLabel} trade`);
      goToErrorPanel();
    }

    setShowCloseButton(true);
  };

  const onBack = useCallback(() => {
    goTo(currentFlowStep - 1);
  }, [goTo, currentFlowStep]);

  const onClose = useCallback(() => closeSidePanel(), [closeSidePanel]);

  const faceValue = assetDetails?.parValue;
  const yieldToWorst = useMemo(
    () =>
      NumberNoNaN(
        market?.lastPrices.find((price) => price.type === AssetPriceType.AssetPriceTypeYieldToWorst)
          ?.price,
      ),
    [market?.lastPrices],
  );

  const breakdown = useFixedIncomeTradeBreakdown({
    side: sidePanelProps.side,
    amount,
    faceValue,
    yieldToWorst,
    tradableUnits,
  });

  const errorMessage = getFixedIncomeTradeAmountErrorMessage(
    breakdown.grossOrderAmount,
    breakdown.netOrderAmount,
    amount,
    tradableUnits,
    sidePanelProps.side === TradeSide.SELL,
  );
  const hasValidationError = Boolean(errorMessage);

  useEffect(() => {
    form.setError(FixedIncomeTradeFormKeys.AMOUNT, {
      message: errorMessage,
    });
  }, [form, rawAmount, errorMessage]);

  useEffect(() => {
    return () => {
      resetOrderCost();
      resetTradeFee();
    };
  }, [resetOrderCost, resetTradeFee]);

  const isContinueDisabled =
    hasValidationError || !previewOrderCost || !form.formState.isValid || isPreviewLoading;

  return (
    <SheetContent className="overflow-auto flex flex-col gap-y-4">
      <div className="flex flex-col flex-1">
        {currentFlowStep !== TradeFormSteps.SUCCESS && currentFlowStep !== TradeFormSteps.ERROR && (
          <FixedIncomeOverview
            noActions
            longNameFormat
            securityId={securityId}
            showBidAsk={
              currentFlowStep === TradeFormSteps.FORM || currentFlowStep === TradeFormSteps.SUMMARY
            }
          />
        )}

        <FormProvider {...form}>
          <form className="flex flex-col flex-1 justify-between">
            {currentFlowStep === TradeFormSteps.FORM && (
              <FixedIncomeTradeForm
                side={sidePanelProps.side}
                asset={assetData}
                breakdown={breakdown}
              />
            )}

            {currentFlowStep === TradeFormSteps.SUMMARY && (
              <FixedIncomeTradeSummary
                title={t('miscScreens.tradeOptions.fixedIncome.tradeSummary.orderSummary')}
                data={{
                  orderType: sidePanelProps.side,
                  quantity: amount,
                  yieldMaturity: assetDetails?.yieldMaturity,
                  yieldToWorst: breakdown.yieldToWorst,
                  limitPrice: form.getValues(FixedIncomeTradeFormKeys.LIMIT_PRICE),
                  issuerName: assetDetails?.issuer ?? '',
                  couponRate: assetDetails?.couponRate,
                  maturityDate: assetDetails?.maturityDate,
                  isin: assetData?.isin ?? '',
                  faceValuePerBond: breakdown?.faceValue ?? 0,
                  accruedInterestPerBond: breakdown.accruedInterestPerBond,
                  markup: breakdown.markup,
                  commission: breakdown.commission,
                  // These values are seemingly inverted because apparently it's how other platforms show them
                  // Please refer to this Slack thread: https://apexclearing.slack.com/archives/C08S2MKVBH7/p1759767528196939?thread_ts=1759412311.869139&cid=C08S2MKVBH7
                  orderValue: breakdown.netOrderAmount,
                  netAmount: breakdown.grossOrderAmount,
                }}
                asOf={new Date()}
                layoutType="grid"
                isEstimated
              />
            )}

            {currentFlowStep === TradeFormSteps.SUCCESS && (
              <FixedIncomeTradeSuccess onClose={onClose} />
            )}
            {currentFlowStep === TradeFormSteps.ERROR && <FixedIncomeTradeError />}
          </form>
        </FormProvider>
      </div>

      <SheetFooter className="text-center mt-10">
        <div className="flex w-full flex-col gap-y-2">
          <Button
            onClick={currentFlowStep === TradeFormSteps.SUMMARY ? onSubmit : onContinue}
            disabled={isContinueDisabled}
          >
            {currentFlowStep === TradeFormSteps.FORM
              ? t('common.continue')
              : t('common.submitBtnText')}
          </Button>

          {currentFlowStep === TradeFormSteps.SUMMARY && (
            <Button onClick={onBack} type="button" variant="secondary">
              {t('common.back')}
            </Button>
          )}
        </div>
      </SheetFooter>
    </SheetContent>
  );
};
